import React from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiGrid, FiMap, FiActivity, FiSettings, FiCpu, FiPlayCircle } = FiIcons;

const navItems = [
  { path: '/dashboard', icon: FiGrid, label: 'Control' },
  { path: '/planner', icon: FiMap, label: 'Transit' },
  { path: '/analytics', icon: FiActivity, label: 'Metrics' },
  { path: '/demo', icon: FiPlayCircle, label: 'Demo' },
  { path: '/settings', icon: FiSettings, label: 'System' },
];

export default function Layout() {
  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-neutral-950 text-neutral-200 font-sans selection:bg-emerald-900 selection:text-emerald-100">
      {/* Sidebar (Desktop) */}
      <nav className="hidden md:flex flex-col w-64 border-r border-neutral-800/50 bg-neutral-950/80 backdrop-blur-xl p-4 sticky top-0 h-screen z-50">
        <div className="flex items-center gap-3 mb-12 px-2 pt-4">
          <div className="p-2 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
             <SafeIcon icon={FiCpu} className="text-2xl text-emerald-400 drop-shadow-[0_0_8px_rgba(52,211,153,0.5)]" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-neutral-100">CalmCommute</h1>
            <p className="text-xs text-neutral-500 uppercase tracking-widest font-mono">Terminal v2.4</p>
          </div>
        </div>

        <div className="flex flex-col gap-2 flex-grow">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 relative overflow-hidden group ${
                  isActive 
                    ? 'text-emerald-400 bg-neutral-900/50 border border-emerald-500/20' 
                    : 'text-neutral-500 hover:text-neutral-300 hover:bg-neutral-900/30'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <motion.div 
                      layoutId="active-nav"
                      className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.8)]"
                      initial={false}
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                  <SafeIcon icon={item.icon} className={`text-lg ${isActive ? 'drop-shadow-[0_0_5px_rgba(52,211,153,0.5)]' : ''}`} />
                  <span className="font-medium tracking-wide">{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>

        <div className="mt-auto pt-4 border-t border-neutral-800/50 text-center">
          <p className="text-[10px] text-neutral-600 uppercase tracking-widest">System Online</p>
        </div>
      </nav>

      {/* Main Content Area */}
      <main className="flex-1 relative overflow-x-hidden pt-4 pb-24 md:pb-4 md:pt-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-neutral-900/40 via-neutral-950 to-neutral-950 pointer-events-none -z-10"></div>
        <div className="p-4 md:p-8 max-w-7xl mx-auto min-h-full flex flex-col">
          <Outlet />
          
          {/* Global Footer */}
          <footer className="mt-auto pt-12 pb-4 text-center">
            <p className="text-xs text-neutral-600 font-mono">
              Made by LingLink AI and DigLab AI
            </p>
          </footer>
        </div>
      </main>

      {/* Bottom Nav (Mobile) */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-neutral-950/90 backdrop-blur-xl border-t border-neutral-800/80 z-50 px-4 py-3 pb-safe">
        <div className="flex justify-between items-center max-w-md mx-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 p-2 transition-colors ${
                  isActive ? 'text-emerald-400' : 'text-neutral-500'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <SafeIcon icon={item.icon} className={`text-xl ${isActive ? 'drop-shadow-[0_0_8px_rgba(52,211,153,0.6)]' : ''}`} />
                  <span className="text-[10px] font-medium uppercase tracking-wider">{item.label}</span>
                  {isActive && (
                    <motion.div 
                      layoutId="mobile-active"
                      className="w-1 h-1 rounded-full bg-emerald-400 mt-1 shadow-[0_0_5px_rgba(52,211,153,1)]"
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}